import {useQuery, useQueryClient} from "@tanstack/react-query";
import {produce, type WritableDraft} from "immer";
import {merge} from "lodash";
import {useEffect, useState} from "react";

const useSSE = <TData, TQueryData = TData>({
  initialState,
  queryKey,
  enabled = true,
  onError,
  onSuccess,
}: {
  initialState: TQueryData;
  queryKey: string[];
  enabled?: boolean;
  onError?: () => void;
  onSuccess?: () => void;
}) => {
  const [isPending, setPending] = useState(false);
  const queryClient = useQueryClient();
  const {data} = useQuery<TQueryData>({
    queryKey,
    queryFn: () => initialState,
  });

  useEffect(() => {
    if (!enabled) return;
    setPending(true);
    const url = `${import.meta.env.VITE_APP_API_BASE_URL}/api${queryKey[0]}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = event => {
      const data = JSON.parse(event.data) as TData & {done: boolean};

      queryClient.setQueryData<WritableDraft<TQueryData>>(queryKey, prevState =>
        produce(prevState, state => {
          if (state instanceof Array) {
            state?.push(data);
            return;
          }

          merge(state, data);
        }),
      );

      if (data.done) {
        onSuccess?.();
        eventSource.close();
        setPending(false);
      }
    };

    eventSource.onerror = e => {
      console.error("SSE connection failed:", e);
      onError?.();
      eventSource.close();
      setPending(false);
    };

    return () => eventSource.close();
  }, [enabled, queryClient, queryKey]);

  return {data, isPending};
};

export {useSSE};
